<template>
  <NavigationMenu>
    <NavigationMenuList>
      <NavigationMenuItem>
        <NavigationMenuTrigger
          class="capitalize px-2 py-2.5 gap-x-2 text-[#344054]"
        >
          {{ $t("mockups", "Mockups") }}
        </NavigationMenuTrigger>
        <NavigationMenuContent>
          <div class="flex gap-3 py-3 px-2 w-min">
            <div class="flex flex-col gap-2">
              <Menu
                v-for="(_, category) in categoriesList"
                :key="category"
                as="div"
              >
                <div>
                  <MenuButton
                    class="flex py-1.5 px-2 items-center justify-between w-40 rounded-sm"
                    :class="{
                      'bg-[#F9FAFB]': String(category) === openCategory,
                    }"
                    @mouseenter="toggleSubcategory(category)"
                  >
                    <span class="text-sm capitalize whitespace-nowrap">
                      {{ $t(String(category), category) }}
                    </span>
                    <Icon icon="mdi:chevron-right" height="16" />
                  </MenuButton>
                </div>
              </Menu>
            </div>
            <div class="border-r border-[#EBECEE]"></div>
            <div
              class="flex flex-col gap-y-2 gap-x-1 text-left text-label-lg h-60 w-max flex-wrap"
            >
              <Menu
                v-for="(item, index) in categoriesList[openCategory]"
                :key="index"
                class="flex"
              >
                <router-link
                  :to="{
                    name: 'subcategory',
                    params: {
                      category: openCategory,
                      subcategory: item,
                    },
                  }"
                  class="px-2 py-1.5 whitespace-nowrap hover:bg-[#F9FAFB] rounded-sm min-w-max"
                  :class="{
                    'font-bold text-text-content bg-background-secondary':
                      item === openSubcategory,
                  }"
                >
                  <span
                    :class="{
                      capitalize: item !== 'iPhone',
                    }"
                  >
                    {{ $t(item) }}
                  </span>
                  <div class="pr-9"></div>
                </router-link>
              </Menu>
            </div>
          </div>
        </NavigationMenuContent>
      </NavigationMenuItem>
      <NavigationMenuItem>
        <NavigationMenuTrigger class="px-2 py-2.5 gap-x-2 text-[#344054]">
          {{ $t("navbar.tools", "Tools") }}
        </NavigationMenuTrigger>
        <NavigationMenuContent>
          <div class="flex gap-3 py-3 px-2 w-min">
            <div class="flex gap-3">
              <div class="flex flex-col gap-2.5">
                <Menu v-for="(value, key) in tools" :key="key" as="div">
                  <div>
                    <MenuButton
                      class="flex py-1.5 px-2 items-center justify-between w-40 rounded-sm"
                      :class="{ 'bg-[#F9FAFB]': key === valueOpen }"
                      @mouseenter="toggleValue(key)"
                    >
                      <span class="text-sm capitalize whitespace-nowrap">
                        {{ $t(`navbar.${key}`, key) }}
                      </span>
                      <Icon icon="mdi:chevron-right" height="16" />
                    </MenuButton>
                  </div>
                </Menu>
              </div>
              <div class="border-r border-[#EBECEE]"></div>
              <div
                v-if="openValue"
                class="flex flex-col gap-2.5 text-left text-label-lg h-40 w-max flex-wrap"
              >
                <div v-for="(item, index) in tools[valueOpen]" :key="index">
                  <div class="flex items-center gap-1 pr-2">
                    <router-link
                      v-if="item?.to"
                      :to="item.to"
                      class="text-sm py-1.5 px-2 hover:bg-[#F9FAFB] rounded-sm"
                    >
                      {{ $t(`navbar.${item.name}`, item.name) }}
                    </router-link>
                    <NavigationMenuLink
                      v-else
                      :href="item.link"
                      target="_blank"
                      class="text-sm py-1.5 px-2 hover:bg-[#F9FAFB] rounded-sm"
                    >
                      {{ $t(`navbar.${item.name}`, item.name) }}
                    </NavigationMenuLink>
                    <div
                      v-if="item.upcomingText"
                      class="text-[#6E6E6E] text-xs leading-4 bg-[#F7F9FB] rounded-full font-semibold"
                    >
                      <span class="py-0.5 px-2.5">
                        {{
                          $t(`navbar.${item.upcomingText}`, item.upcomingText)
                        }}
                      </span>
                    </div>
                    <div v-else class="pr-9"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </NavigationMenuContent>
      </NavigationMenuItem>
    </NavigationMenuList>
  </NavigationMenu>
</template>

<script setup lang="ts">
import { ref, watch, onMounted } from "vue";
import tools from "@/data/toolsData.json";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";
import { Menu, MenuButton } from "@headlessui/vue";
import { useRoute } from "vue-router";
import { Icon } from "@iconify/vue";
import { categoriesList } from "@/util/common.util";

const route = useRoute();

const openCategory: any = ref("apparel");
const valueOpen = ref(null);
const openSubcategory = ref(null);
const openValue = ref(false);

const toggleSubcategory = (category) => {
  openCategory.value = category;
};

const toggleValue = (key) => {
  openValue.value = true;
  valueOpen.value = key;
};

watch(
  () => route.params,
  (newParams) => {
    if (newParams.category && newParams.category !== openCategory.value) {
      openCategory.value = newParams.category || "apparel";
      if (newParams.subcategory) {
        openSubcategory.value = newParams.subcategory;
      }
    }
    if (newParams.subcategory) {
      openSubcategory.value = newParams.subcategory;
    }
  },
);

onMounted(() => {
  const routeParams = route.params;
  openCategory.value = routeParams.category || "apparel";
  openSubcategory.value = routeParams.subcategory;

  const initialTool = Object.keys(tools)[0];
  valueOpen.value = initialTool;
  openValue.value =
    !!routeParams.value || Object.keys(tools).includes(initialTool);
});
</script>
